import { useEffect, useCallback } from "react";

export type PrivacyPolicyBlock =
  | {
      type: "heading" | "subheading" | "paragraph";
      content: string;
    }
  | {
      type: "list";
      content: string[];
    };

export interface PrivacyPolicyModalData {
  title: string;
  date: string;
  description: PrivacyPolicyBlock[];
}

interface PrivacyPolicyModalProps {
  data: PrivacyPolicyModalData | null;
  onClose: () => void;
}

export default function PrivacyPolicyModal({
  data,
  onClose,
}: PrivacyPolicyModalProps) {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    },
    [onClose]
  );

  useEffect(() => {
    if (!data) return;

    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [data, handleKeyDown]);

  if (!data) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-foreground-950/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal card */}
      <div className="relative w-full max-w-[600px] max-h-[85vh] bg-background-50 rounded-2xl overflow-hidden shadow-2xl animate-scale-in">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-20 w-9 h-9 rounded-full bg-background-50/90 backdrop-blur-sm border border-background-200/60 flex items-center justify-center hover:bg-background-100 transition-colors duration-200 cursor-pointer"
          aria-label="Close modal"
        >
          <i className="ri-close-line text-foreground-700 w-4 h-4 flex items-center justify-center" />
        </button>

        {/* Header banner */}
        <div className="relative h-44 overflow-hidden">
          <div className="absolute inset-0 bg-[linear-gradient(135deg,oklch(var(--primary-700)/0.9),oklch(var(--background-800)/0.95))]">
            <div
              className="absolute inset-0 opacity-[0.06]"
              style={{
                backgroundImage:
                  "repeating-linear-gradient(45deg, transparent, transparent 20px, oklch(var(--background-50)/0.5) 20px, oklch(var(--background-50)/0.5) 21px)",
              }}
            />
          </div>

          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/50 to-transparent" />

          {/* Title */}
          <div className="absolute bottom-6 left-6">
            <span className="inline-block px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-background-50/90 border border-background-200/40 text-foreground-700 mb-2">
              Legal
            </span>

            <h3 className="text-sm md:text-xl font-heading font-bold text-foreground-950">
              {data.title}
            </h3>

            <p className="text-sm text-foreground-600 mt-1">
              {data.date}
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-6 overflow-y-auto max-h-[calc(85vh-11rem)]">
          <div className="text-sm md:text-sm text-foreground-600 leading-relaxed space-y-2 mb-8">
            {data.description.map((block, index) => {
              switch (block.type) {
                case "heading":
                  return (
                    <h4
                      key={index}
                      className="text-sm font-semibold text-foreground-950 mt-2"
                    >
                      {block.content}
                    </h4>
                  );

                case "subheading":
                  return (
                    <h6
                      key={index}
                      className="text-sm font-semibold text-foreground-900 mt-2"
                    >
                      {block.content}
                    </h6>
                  );

                case "list":
                  return (
                    <ul
                      key={index}
                      className="list-disc pl-5 space-y-2"
                    >
                      {block.content.map((item, itemIndex) => (
                        <li key={itemIndex}>{item}</li>
                      ))}
                    </ul>
                  );

                case "paragraph":
                default:
                  return (
                    <p
                      key={index}
                      className="whitespace-pre-line"
                    >
                      {block.content}
                    </p>
                  );
              }
            })}
          </div>

          {/* CTA */}
          <a
            href="#cta"
            onClick={(e) => {
              e.preventDefault();

              onClose();

              setTimeout(() => {
                document
                  .querySelector("#cta")
                  ?.scrollIntoView({ behavior: "smooth" });
              }, 200);
            }}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-foreground-950 text-background-50 dark:text-foreground-950 dark:bg-foreground-50 dark:hover:bg-foreground-200 text-sm font-semibold hover:bg-foreground-800 transition-colors duration-200 whitespace-nowrap cursor-pointer"
          >
            Get a Free Consultation
            <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
          </a>
        </div>
      </div>
    </div>
  );
}